/**
 * `/firms` — the identity firm-picker (ADR-0022 B10; sign-in-once → firm-picker →
 * select). After an identity sign-in, this lists the identity's ACTIVE firm memberships
 * (worker-auth `GET /auth/firms`; fixtures: firms, firms-firm-name-unresolved) by the firm's
 * display name (its id when the name is not resolvable) and, on selection, mints a firm-scoped
 * session (`POST /auth/firms/{firm_id}/select` → the compound `argus_firm` cookie), loads that
 * firm's principal (worker-api `GET /api/v1/auth/me` — the per-firm role) and lands in the app
 * bound to that firm. It is also where "Switch firm" (top nav) returns to: selecting another
 * firm is a brand-new firm session, never a re-point of the current one.
 *
 *  - A SINGLE-membership identity AUTO-SELECTS — no picker is shown; it is a transparent
 *    hop to /dashboard (the common case is indistinguishable from the legacy flow).
 *  - A suspended/terminated firm is rendered NON-SELECTABLE (`isFirmSelectable`). The
 *    select chokepoint is the fail-closed authority (it 403s a non-active firm — firms.ts
 *    step 3); this client guard mirrors it so the row is never actionable in the first
 *    place, and a defensive select failure still surfaces a non-revealing error (CC-7).
 *  - Zero memberships → a non-revealing "no active firm access" message (never enumerated).
 *  - A feed that cannot be loaded — a transport/server error, or a body that is not the
 *    contract's `{ firms: [...] }` shape — → a clear "couldn't load your firms" state with a
 *    retry (never a crash on a malformed list).
 *
 * INVITATIONS (PO ruling 2026-09-23). Alongside the memberships the picker loads the identity's
 * LIVE invitations (worker-auth `GET /auth/invitations`) and lists them in an "Invitations"
 * group — firm, role, expiry, and an Accept that performs the grant by id
 * (`POST /auth/invitations/{invitation_id}/accept`, the same grant the emailed token performs).
 *  - The two feeds load IN PARALLEL and the invitations never gate the firms: a lone
 *    membership auto-selects the moment the firms feed lands, and an invitations failure is
 *    a small inline note with its own retry, never the firms error state.
 *  - No memberships but live invitations → a "You've been invited" card listing them (the
 *    "No firm access" state is kept for an identity with neither).
 *  - After an accept the firms feed is re-read: the accepted firm becomes the ONLY membership →
 *    it auto-selects exactly like a lone membership; otherwise it joins the list and its
 *    invitation row goes. 404 → the row goes with "That invitation is no longer available."
 *    401 `second_factor_required` (a password-only session on an enrolled identity) → the
 *    explanation + "Sign in again" (sign out, then /login carrying redirect=/firms) — the same
 *    copy the invite screen uses; a password-only session cannot be stepped up in place.
 *
 * After selecting, the picker lands on /dashboard — or on the allowlisted screen a re-sign-in
 * started from (`?redirect=/settings/security`; router/redirect.ts).
 *
 * An expired identity session (401 on the feed) signs out locally and returns to /login.
 */
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState, type JSX } from 'react';

import { AuthCard, FormError, SubmitButton } from '../components/ui.tsx';
import {
  api,
  getErrorMessage,
  inviteSignInReason,
  isApiError,
  type FirmMembership,
  type PendingInvitation,
} from '../lib/api.ts';
import { formatRole } from '../lib/format/role.ts';
import { returnPathOf } from '../router/redirect.ts';
import { useAuth } from '../store/auth-store.ts';

/** Shown when the firm feed is unreadable (non-revealing — no status, no body). */
const FEED_UNREADABLE = "We couldn't load your firms. Please try again.";
/** The inline note when the invitations feed is unreadable (the firm list stays usable). */
const INVITATIONS_UNREADABLE = "Couldn't load your invitations";
/** Shown when an accept 404s: not this identity's, expired, revoked or already accepted. */
const INVITATION_GONE = 'That invitation is no longer available.';
const STEP_UP_REQUIRED =
  'For your security, accepting needs a sign-in that used your authenticator app. Sign in again with your authenticator code (or a recovery code) to accept.';

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * A firm is selectable unless the feed reports a non-active authoritative status. A
 * suspended/terminated firm's select POST 403s (firms.ts step 3), so it is fail-closed
 * here too. An ABSENT status (the current feed shape — see `FirmMembership`) is treated
 * as selectable; the select POST remains the authority.
 */
export function isFirmSelectable(firm: FirmMembership): boolean {
  return firm.firm_status !== 'suspended' && firm.firm_status !== 'terminated';
}

/**
 * The feed's `firms` array when the response is the contract's shape (fixtures: firms,
 * firms-firm-name-unresolved) — every row carrying the string `firm_id` + `role` the picker
 * renders and selects with, and `firm_name` a string when present — else null. worker-auth is
 * held to that shape by its own suite; this is the SPA refusing to crash if anything in between
 * (a proxy, a stale deploy, a partial outage) hands it something else.
 */
function readFirmsFeed(res: unknown): FirmMembership[] | null {
  if (typeof res !== 'object' || res === null) return null;
  const firms = (res as { firms?: unknown }).firms;
  if (!Array.isArray(firms)) return null;
  const wellFormed = firms.every((row: unknown) => {
    if (typeof row !== 'object' || row === null) return false;
    const r = row as Record<string, unknown>;
    return (
      typeof r['firm_id'] === 'string' &&
      typeof r['role'] === 'string' &&
      (r['firm_name'] === undefined || typeof r['firm_name'] === 'string')
    );
  });
  return wellFormed ? (firms as FirmMembership[]) : null;
}

/**
 * The invitations feed's `invitations` array when the response is the contract's shape
 * (fixtures: invitations, invitations-empty) — every row carrying the string `invitation_id`
 * (what accept is keyed on), `firm_id` + `role`, a numeric `expires_at`, and `firm_name` a
 * string when present — else null (rendered as the inline load-failure note, never a crash).
 */
function readInvitationsFeed(res: unknown): PendingInvitation[] | null {
  if (typeof res !== 'object' || res === null) return null;
  const invitations = (res as { invitations?: unknown }).invitations;
  if (!Array.isArray(invitations)) return null;
  const wellFormed = invitations.every((row: unknown) => {
    if (typeof row !== 'object' || row === null) return false;
    const r = row as Record<string, unknown>;
    return (
      typeof r['invitation_id'] === 'string' &&
      typeof r['firm_id'] === 'string' &&
      typeof r['role'] === 'string' &&
      typeof r['expires_at'] === 'number' &&
      (r['firm_name'] === undefined || typeof r['firm_name'] === 'string')
    );
  });
  return wellFormed ? (invitations as PendingInvitation[]) : null;
}

/** The firm's human label — the display name when the feed carries it, else the id. */
function firmLabel(firm: { firm_id: string; firm_name?: string }): string {
  return firm.firm_name ?? firm.firm_id;
}

/** The mark on a firm card: the initials of the first two words, else the first two characters. */
function firmInitials(label: string): string {
  const words = label.split(/[\s&/,.\-–—]+/).filter((w) => /[a-z0-9]/i.test(w));
  const initials = words
    .slice(0, 2)
    .map((w) => w.charAt(0))
    .join('');
  return (initials.length >= 2 ? initials : label.slice(0, 2)).toUpperCase();
}

/**
 * "Expires in N days" / "Expires in 1 day" / "Expires today" for a live invitation's
 * `expires_at` (unix seconds). Whole days remaining; under a day is "today" (the feed serves
 * live invitations only, so a past instant is at most a clock skew and reads as today).
 */
export function formatInvitationExpiry(expiresAtSeconds: number, now = Date.now()): string {
  const days = Math.floor((expiresAtSeconds * 1000 - now) / DAY_MS);
  if (days < 1) return 'Expires today';
  return days === 1 ? 'Expires in 1 day' : `Expires in ${days} days`;
}

/** The invitations feed as the picker holds it — loading, readable, or not. */
type InvitationsState =
  | { status: 'loading' }
  | { status: 'ready'; rows: PendingInvitation[] }
  | { status: 'failed' };

const choiceStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  width: '100%',
  textAlign: 'left' as const,
  padding: '12px 14px',
  borderRadius: 8,
  border: '1px solid var(--auth-field-border, #dcd6c8)',
  background: 'var(--auth-field-bg, #fff)',
  color: 'var(--auth-field-text, #1c2536)',
  fontSize: 14,
  fontFamily: 'inherit',
};

const markStyle = {
  flex: 'none',
  width: 36,
  height: 36,
  borderRadius: 8,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'var(--auth-btn-bg, #1e2a44)',
  color: 'var(--auth-btn-text, #fdfdfb)',
  fontSize: 13,
  fontWeight: 700,
  letterSpacing: '0.04em',
};

const titleStyle = {
  fontWeight: 600,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
};

const mutedStyle = { fontSize: 12, color: 'var(--auth-muted, #7a7365)' };

const groupHeadingStyle = {
  margin: 0,
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase' as const,
  color: 'var(--auth-muted, #7a7365)',
};

export function FirmPickerScreen(): JSX.Element {
  const navigate = useNavigate();
  const { redirect } = useSearch({ strict: false }) as { redirect?: string };
  const destination = returnPathOf(redirect) ?? '/dashboard';
  const selectFirm = useAuth((s) => s.selectFirm);
  const signOut = useAuth((s) => s.signOut);
  const [firms, setFirms] = useState<FirmMembership[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // 401 second_factor_required on accept: the identity must sign in again WITH its authenticator.
  const [needsStepUp, setNeedsStepUp] = useState(false);
  // The feed could not be read (an error or a malformed body) — distinct from "still loading".
  const [loadFailed, setLoadFailed] = useState(false);
  // Bumped by "Try again" to re-run the feed load.
  const [attempt, setAttempt] = useState(0);
  const [selectingId, setSelectingId] = useState<string | null>(null);
  const [invitations, setInvitations] = useState<InvitationsState>({ status: 'loading' });
  // Bumped by the invitations note's "Retry" to re-run ONLY the invitations load.
  const [invitationsAttempt, setInvitationsAttempt] = useState(0);
  const [acceptingId, setAcceptingId] = useState<string | null>(null);
  // Guard the single-membership auto-select so it fires at most once (StrictMode double
  // effect / re-renders never re-trigger a second mint).
  const autoSelected = useRef(false);

  const land = useCallback(
    async (firm: FirmMembership): Promise<void> => {
      setError(null);
      setSelectingId(firm.firm_id);
      try {
        await selectFirm(firm);
        await navigate({ to: destination });
      } catch (err) {
        setSelectingId(null);
        setError(getErrorMessage(err));
      }
    },
    [selectFirm, navigate, destination],
  );

  /**
   * Take a freshly read firms feed on board. A lone selectable membership auto-selects (once) —
   * on the initial load AND after an accept that made the accepted firm the only membership.
   */
  const adoptFirms = useCallback(
    (feed: FirmMembership[]): void => {
      setFirms(feed);
      // Single active membership → auto-select (once). A lone suspended firm is NOT
      // auto-selected — it falls through to the non-selectable list state.
      const only = feed.length === 1 ? feed[0] : undefined;
      if (only && isFirmSelectable(only) && !autoSelected.current) {
        autoSelected.current = true;
        void land(only);
      }
    },
    [land],
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res: unknown = await api.auth.firms();
        if (cancelled) return;
        const feed = readFirmsFeed(res);
        if (feed === null) {
          setLoadFailed(true);
          setError(FEED_UNREADABLE);
          return;
        }
        adoptFirms(feed);
      } catch (err) {
        if (cancelled) return;
        if (isApiError(err) && err.status === 401) {
          // The identity session is gone — nothing to pick from.
          await signOut();
          await navigate({ to: '/login' });
          return;
        }
        setLoadFailed(true);
        setError(getErrorMessage(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [adoptFirms, signOut, navigate, attempt]);

  // The invitations feed: its own effect, so it starts alongside the firms feed (never after
  // it) and its outcome never touches the firms state — the lone-membership auto-select above
  // fires as soon as the firms land, whatever this feed is doing.
  useEffect(() => {
    let cancelled = false;
    setInvitations({ status: 'loading' });
    void (async () => {
      try {
        const res: unknown = await api.auth.invitations();
        if (cancelled) return;
        const rows = readInvitationsFeed(res);
        setInvitations(rows === null ? { status: 'failed' } : { status: 'ready', rows });
      } catch {
        // A 401 here is the same expired session the firms feed handles; anything else is a
        // note, never the firms error state.
        if (!cancelled) setInvitations({ status: 'failed' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [invitationsAttempt]);

  function retryLoad(): void {
    setError(null);
    setLoadFailed(false);
    setAttempt((n) => n + 1);
  }

  function retryInvitations(): void {
    setInvitationsAttempt((n) => n + 1);
  }

  function dropInvitation(invitationId: string): void {
    setInvitations((s) =>
      s.status === 'ready'
        ? { status: 'ready', rows: s.rows.filter((r) => r.invitation_id !== invitationId) }
        : s,
    );
  }

  async function accept(invitation: PendingInvitation): Promise<void> {
    setError(null);
    setAcceptingId(invitation.invitation_id);
    try {
      await api.auth.acceptInvitation(invitation.invitation_id);
      dropInvitation(invitation.invitation_id);
      // The grant is made: re-read the memberships so the new firm is listed from the feed
      // (never synthesised client-side) — and a now-lone membership auto-selects.
      const res: unknown = await api.auth.firms();
      const feed = readFirmsFeed(res);
      if (feed === null) {
        setError(FEED_UNREADABLE);
        return;
      }
      adoptFirms(feed);
    } catch (err) {
      if (isApiError(err) && err.status === 404) {
        dropInvitation(invitation.invitation_id);
        setError(INVITATION_GONE);
        return;
      }
      const reason = inviteSignInReason(err);
      if (reason === 'second_factor_required') {
        // A password-only session on an enrolled identity cannot be stepped up in place: explain,
        // then offer a fresh sign-in (with the authenticator) that returns here.
        setError(STEP_UP_REQUIRED);
        setNeedsStepUp(true);
        return;
      }
      if (reason === 'session_required') {
        await signOut();
        await navigate({ to: '/login' });
        return;
      }
      setError(getErrorMessage(err));
    } finally {
      setAcceptingId(null);
    }
  }

  async function handleSignOut(): Promise<void> {
    await signOut();
    await navigate({ to: '/login' });
  }

  async function signOutThenSignIn(): Promise<void> {
    await signOut();
    await navigate({ to: '/login', search: { redirect: '/firms' } });
  }
  const stepUpAction = needsStepUp ? (
    <SubmitButton type="button" onClick={() => void signOutThenSignIn()}>
      Sign in again
    </SubmitButton>
  ) : null;

  const signOutLink = (
    <button
      type="button"
      onClick={() => void handleSignOut()}
      style={{
        marginTop: 16,
        padding: 0,
        border: 0,
        background: 'transparent',
        color: 'var(--auth-link, #3a5a9a)',
        cursor: 'pointer',
        fontSize: 13,
        fontFamily: 'inherit',
      }}
    >
      Sign out
    </button>
  );

  if (firms === null && loadFailed) {
    return (
      <AuthCard
        title="Couldn't load your firms"
        subtitle="Your firm list isn't available right now."
      >
        <div style={{ display: 'grid', gap: 12 }}>
          <FormError message={error ?? FEED_UNREADABLE} />
          <SubmitButton type="button" onClick={retryLoad}>
            Try again
          </SubmitButton>
        </div>
        {signOutLink}
      </AuthCard>
    );
  }

  // No memberships: wait for the invitations feed too, so the card that appears is the right
  // one ("You've been invited" vs "No firm access") — never a flash of the wrong one.
  if (firms === null || (firms.length === 0 && invitations.status === 'loading')) {
    return (
      <AuthCard title="Signing you in" subtitle="Loading your firms…">
        <FormError message={error} />
      </AuthCard>
    );
  }

  const busy = selectingId !== null || acceptingId !== null;
  const pendingInvitations = invitations.status === 'ready' ? invitations.rows : [];

  const invitationsNote =
    invitations.status === 'failed' ? (
      <p
        role="status"
        style={{
          margin: 0,
          display: 'flex',
          alignItems: 'baseline',
          gap: 8,
          fontSize: 13,
          color: 'var(--auth-muted, #7a7365)',
        }}
      >
        <span>{INVITATIONS_UNREADABLE}.</span>
        <button
          type="button"
          onClick={retryInvitations}
          style={{
            padding: 0,
            border: 0,
            background: 'transparent',
            color: 'var(--auth-link, #3a5a9a)',
            cursor: 'pointer',
            fontSize: 13,
            fontFamily: 'inherit',
          }}
        >
          Retry
        </button>
      </p>
    ) : null;

  const invitationRows = pendingInvitations.map((invitation) => {
    const label = firmLabel(invitation);
    const accepting = acceptingId === invitation.invitation_id;
    return (
      // The same card as a firm choice, but a static row (only its Accept is actionable) — so
      // it carries the choice STYLE without the choice class's hover affordance.
      <div
        key={invitation.invitation_id}
        aria-busy={accepting}
        style={{ ...choiceStyle, opacity: busy && !accepting ? 0.6 : 1 }}
      >
        <span aria-hidden="true" style={markStyle}>
          {firmInitials(label)}
        </span>
        <span style={{ display: 'grid', gap: 2, minWidth: 0, flex: 1 }}>
          <span style={titleStyle}>{label}</span>
          <span style={mutedStyle}>
            {formatRole(invitation.role)} · {formatInvitationExpiry(invitation.expires_at)}
          </span>
        </span>
        <button
          type="button"
          aria-label={`Accept invitation to ${label}`}
          disabled={busy}
          onClick={() => void accept(invitation)}
          style={{
            flex: 'none',
            padding: '8px 12px',
            borderRadius: 8,
            border: 0,
            background: busy ? 'var(--auth-btn-disabled, #c9c3b5)' : 'var(--auth-btn-bg, #1e2a44)',
            color: 'var(--auth-btn-text, #fdfdfb)',
            cursor: busy ? 'not-allowed' : 'pointer',
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: '0.01em',
            fontFamily: 'inherit',
          }}
        >
          {accepting ? 'Accepting…' : 'Accept'}
        </button>
      </div>
    );
  });

  if (firms.length === 0) {
    if (pendingInvitations.length === 0) {
      return (
        <AuthCard
          title="No firm access"
          subtitle="Your account isn't an active member of any firm yet."
        >
          <div style={{ display: 'grid', gap: 12 }}>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--auth-muted, #7a7365)' }}>
              Ask a firm administrator to invite you, then sign in again.
            </p>
            {invitationsNote}
            <FormError message={error} />
            {stepUpAction}
          </div>
          {signOutLink}
        </AuthCard>
      );
    }
    return (
      <AuthCard
        title="You've been invited"
        subtitle="Accept an invitation to start working in that firm."
      >
        <div role="group" aria-label="Invitations" style={{ display: 'grid', gap: 10 }}>
          {invitationRows}
        </div>
        <div style={{ marginTop: 16 }}>
          <FormError message={error} />
          {stepUpAction}
        </div>
        {signOutLink}
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Choose a firm" subtitle="Select the firm you want to work in.">
      <div role="group" aria-label="Your firms" style={{ display: 'grid', gap: 10 }}>
        {firms.map((firm) => {
          const selectable = isFirmSelectable(firm);
          const label = firmLabel(firm);
          const selecting = selectingId === firm.firm_id;
          return (
            <button
              key={firm.firm_id}
              type="button"
              className="argus-auth__choice"
              aria-label={`Select ${label}`}
              disabled={!selectable || busy}
              onClick={() => void land(firm)}
              style={{ ...choiceStyle, cursor: !selectable || busy ? 'not-allowed' : 'pointer' }}
            >
              <span aria-hidden="true" style={markStyle}>
                {firmInitials(label)}
              </span>
              <span style={{ display: 'grid', gap: 2, minWidth: 0, flex: 1 }}>
                <span style={titleStyle}>{label}</span>
                <span style={mutedStyle}>{formatRole(firm.role)}</span>
                {selectable ? null : (
                  <span style={{ fontSize: 12, color: 'var(--auth-danger, #b42318)' }}>
                    Access to this firm is suspended. Contact your firm administrator.
                  </span>
                )}
              </span>
              {selectable ? (
                <span
                  aria-hidden="true"
                  style={{ flex: 'none', fontSize: 13, color: 'var(--auth-link, #3a5a9a)' }}
                >
                  {selecting ? 'Selecting…' : '→'}
                </span>
              ) : (
                <span
                  style={{
                    flex: 'none',
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: 'var(--auth-danger, #b42318)',
                  }}
                >
                  Suspended
                </span>
              )}
            </button>
          );
        })}
      </div>
      {pendingInvitations.length > 0 ? (
        <div
          role="group"
          aria-label="Invitations"
          style={{ display: 'grid', gap: 10, marginTop: 20 }}
        >
          <p style={groupHeadingStyle}>Invitations</p>
          {invitationRows}
        </div>
      ) : null}
      {invitationsNote ? <div style={{ marginTop: 16 }}>{invitationsNote}</div> : null}
      <div style={{ marginTop: 16 }}>
        <FormError message={error} />
        {stepUpAction}
      </div>
      {signOutLink}
    </AuthCard>
  );
}
